import { SummaryList } from 'nhsuk-react-components';
import { useNavigate } from 'react-router-dom';
import RoutingPlansList from 'components/RoutingPlans';
import { RoutingPlans } from 'schemas/RoutingPlan.schema.ts';
import featureFlags from 'services/featureFlags.ts';
import { CampaignTemplate } from 'schemas/CampaignTemplates.schema.ts';
import useCampaignNavigate from 'hooks/useCampaignNavigate.ts';
import { Iteration } from 'schemas/Iteration.schema.ts';
import {
  commsTypeToLabel,
  iterationTypeToLabel,
  YYYYMMDDToHumanReadable,
} from 'utils/formattingUtils.ts';
import SummaryRow from 'components/SummaryTable/Campaign/SummaryRow.tsx';

interface IterationDetailTableProps {
  iteration: Iteration;
  redirectPage?: string;
  canUpdate: boolean;
  teamRoutingPlans?: RoutingPlans;
  template?: CampaignTemplate;
}

export default function IterationDetailTable({
  iteration,
  redirectPage,
  canUpdate,
  teamRoutingPlans,
  template,
}: IterationDetailTableProps) {
  const { getCampaignPathFor } = useCampaignNavigate();
  const navigate = useNavigate();
  const getUpdateHref = (path: string) => getCampaignPathFor(`iteration/${iteration.ID}/${path}${redirectPage ? `?redirectPage=${redirectPage}` : ''}`);

  const handleEdit = async (path: string) => {
    navigate(path);
  };

  const routingIds = iteration.DefaultCommsRouting?.split('|').filter(Boolean) ?? [];

  return (
    <SummaryList>
      <SummaryRow
        itemKey="iteration-name"
        itemName="Iteration name"
        itemValue={iteration.Name}
        handleEdit={() => handleEdit(getUpdateHref('key-details'))}
        canUpdate={canUpdate}
      />
      {!featureFlags.dryRunEnabled && (
        <SummaryRow
          itemKey="iteration-type"
          itemName="Iteration type"
          itemValue={iterationTypeToLabel(iteration.Type as string)}
          handleEdit={() => handleEdit(getUpdateHref('key-details'))}
          canUpdate={canUpdate}
        />
      )}
      <SummaryRow
        itemKey="iteration-comms-type"
        itemName="Communication type"
        itemValue={commsTypeToLabel(iteration.CommsType as string)}
        handleEdit={() => handleEdit(getUpdateHref('key-details'))}
        canUpdate={canUpdate}
      />
      <SummaryRow
        itemKey="iteration-date"
        itemName="Planned run date"
        itemValue={YYYYMMDDToHumanReadable(iteration.IterationDate)}
        handleEdit={() => handleEdit(getUpdateHref('key-details'))}
        canUpdate={canUpdate}
      />
      <SummaryRow
        itemKey="iteration-cohorts"
        itemName="Cohorts"
        itemValue={iteration.IterationCohorts?.map(
          (cohort) => cohort.CohortLabel,
        ).join(', ')}
        handleEdit={() => handleEdit(getUpdateHref('cohorts'))}
        canUpdate={canUpdate && template?.['IterationDetails.IterationCohorts.input.display'] !== 'hidden'}
      />
      <SummaryRow
        itemKey="iteration-routingplans"
        itemName="Default message plans"
        itemValue={(
          <RoutingPlansList
            teamRoutingPlans={teamRoutingPlans ?? []}
            routingIds={routingIds}
          />
        )}
        handleEdit={() => handleEdit(getUpdateHref('message-plan'))}
        canUpdate={canUpdate}
      />
    </SummaryList>
  );
}
